/** @jsx jsx */

import { jsx } from '@balance-web/core';
import { theme } from '@balance-web/theme';

import type { CheckboxSize } from './types';

const sizeToScaleKey = {
  small: 'xxsmall',
  medium: 'xsmall',
} as const;

export const CheckboxIndicator = ({
  size = 'medium',
  ...props
}: {
  children?: React.ReactNode;
  size?: CheckboxSize;
}) => {
  const resolvedSize = theme.sizing[sizeToScaleKey[size]];

  return (
    <div
      css={{
        alignItems: 'center',
        backgroundColor: theme.palette.formControl.background,
        border: `2px solid ${theme.primitives.colour.neutral.k90}`,
        borderRadius: theme.radii.xsmall,
        boxSizing: 'border-box',
        color: theme.palette.formControl.background,
        cursor: 'pointer',
        display: 'flex',
        flexShrink: 0,
        height: resolvedSize,
        justifyContent: 'center',
        width: resolvedSize,
        position: 'relative',

        // For the outter circle on hover
        '::after': {
          content: '""',
          display: 'block',
          width: '190%',
          height: '190%',
          borderRadius: 9999,
          background: '#efefef',
          opacity: 0,
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%,-50%)',
          zIndex: -1,
        },

        'input:not([disabled]):hover + &': {
          '::after': {
            opacity: 0.7,
          },
        },

        'input:not([disabled]):focus + &': {
          boxShadow: `0 0 0 2px ${theme.palette.global.focusRing}`,
        },

        'input + & svg': {
          opacity: 0,
        },
        'input[aria-checked=mixed] + & svg': {
          opacity: 1,
        },
        'input:checked + & svg': {
          opacity: 1,
        },

        'input:checked + &, input:indeterminate + &': {
          backgroundColor: theme.palette.formControl.interaction,
          borderColor: theme.palette.formControl.interaction,
          color: theme.palette.formControl.foregroundChecked,
        },
        'input:disabled + &': {
          backgroundColor: theme.palette.formControl.backgroundDisabled,
          borderColor: theme.palette.formControl.backgroundDisabled,
          color: theme.palette.formControl.foregroundDisabled,
          cursor: 'default',
        },
        'input:checked:disabled + &': {
          color: theme.palette.formControl.foregroundDisabled,
        },
      }}
      {...props}
    />
  );
};
